import React, { useEffect, useRef, useState } from 'react'

import {
  pantryColors,
  pantryLayout,
  pantryRadii,
} from '../../../foundations/tokens'
import { InitialsAvatar } from '../../primitives/InitialsAvatar'

export interface NavItemDef {
  id: string
  label: string
  icon?: React.ReactNode
  active?: boolean
  badge?: number | string
  onClick?: () => void
  subItems?: Omit<NavItemDef, 'subItems'>[]
}

export interface NavSection {
  label?: string
  items: NavItemDef[]
}

export interface NavbarProps {
  sections: NavSection[]
  storeName?: string
  /**
   * Optional storefront URL. When provided together with `storeName`, the
   * header renders a small clickable shortened-store-name link between the
   * brand title and the version string; clicking it opens the URL in a new
   * tab. When omitted, the legacy behaviour (storeName replaces the brand
   * title) is preserved.
   */
  storeUrl?: string
  /**
   * Brand mark image URL (e.g. imported `.svg`). Rendered via CSS mask so it
   * tints with the same `currentColor` as the title (`pantryColors.brand.primary`).
   */
  logoMarkSrc?: string
  /**
   * Optional version string rendered under the logo (e.g. "v1.0.0").
   */
  version?: string
  /**
   * Optional badge rendered flush-right on the logo header row (e.g. a POS
   * integration logo). Rendered as-is; omit to show nothing.
   */
  headerBadge?: React.ReactNode
  user?: {
    initials: string
    name: string
    role?: string
    color?: string
    /** Opens the user profile / "My account" page. */
    onAccountClick?: () => void
    /** Triggers the workspace switcher. */
    onSwitchWorkspaceClick?: () => void
    /** Signs the current user out. */
    onSignOutClick?: () => void
  }
}

/** Mark uses `currentColor` so the square matches the title (`pantryColors.brand.primary`). */
const BreadstackLogo = () => (
  <svg width="34" height="34" viewBox="0 0 28 28" fill="none" aria-hidden>
    <rect width="28" height="28" rx="6" fill="currentColor" />
    <path
      d="M7 14.5L12 19L21 9"
      stroke={pantryColors.white}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const SHORT_STORE_NAME_MAX = 22

const shortenStoreName = (name: string): string => {
  const trimmed = name.trim()
  if (trimmed.length <= SHORT_STORE_NAME_MAX) return trimmed
  return `${trimmed.slice(0, SHORT_STORE_NAME_MAX)}…`
}

export const Navbar: React.FC<NavbarProps> = ({
  sections,
  storeName,
  storeUrl,
  logoMarkSrc,
  version,
  headerBadge,
  user,
}) => {
  const hasStoreShortcut = !!(
    storeName &&
    storeName.trim() &&
    storeUrl &&
    storeUrl.trim()
  )
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!isUserMenuOpen) return
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setIsUserMenuOpen(false)
      }
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsUserMenuOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKey)
    }
  }, [isUserMenuOpen])

  const handleMenuAction = (handler?: () => void) => () => {
    setIsUserMenuOpen(false)
    handler?.()
  }

  return (
    <nav
      style={{
        width: pantryLayout.navWidth,
        minWidth: pantryLayout.navWidth,
        background: pantryColors.white,
        borderRight: `1px solid ${pantryColors.gray[200]}`,
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* ── Logo ── */}
      <div
        style={{
          padding: '18px 20px 14px',
          borderBottom: `1px solid ${pantryColors.gray[200]}`,
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            color: pantryColors.brand.primary,
          }}
        >
          {logoMarkSrc ? (
            <span
              aria-hidden
              style={{
                display: 'inline-block',
                width: 28,
                height: 34,
                flexShrink: 0,
                backgroundColor: 'currentColor',
                maskImage: `url(${logoMarkSrc})`,
                WebkitMaskImage: `url(${logoMarkSrc})`,
                maskSize: 'contain',
                WebkitMaskSize: 'contain',
                maskRepeat: 'no-repeat',
                WebkitMaskRepeat: 'no-repeat',
                maskPosition: 'center',
                WebkitMaskPosition: 'center',
              }}
            />
          ) : (
            <BreadstackLogo />
          )}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              lineHeight: 1.1,
              paddingTop: 2,
              paddingRight: 8,
              flex: 1,
              minWidth: 0,
            }}
          >
            <span
              style={{
                fontWeight: 700,
                fontSize: 20,
                color: pantryColors.brand.primary,
              }}
            >
              Breadstack
            </span>
            {(headerBadge || hasStoreShortcut) && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  marginTop: 2,
                  minWidth: 0,
                  maxWidth: '100%',
                }}
              >
                {hasStoreShortcut && (
                  <a
                    href={storeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={storeName}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 3,
                      fontSize: 9,
                      fontWeight: 500,
                      letterSpacing: '0.01em',
                      lineHeight: 1,
                      color: pantryColors.gray[500],
                      textDecoration: 'none',
                      maxWidth: '100%',
                      minWidth: 0,
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = pantryColors.brand.primary
                      const label = e.currentTarget.querySelector(
                        '[data-store-name-label]',
                      ) as HTMLElement | null
                      if (label) label.style.textDecoration = 'underline'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = pantryColors.gray[500]
                      const label = e.currentTarget.querySelector(
                        '[data-store-name-label]',
                      ) as HTMLElement | null
                      if (label) label.style.textDecoration = 'none'
                    }}
                  >
                    <span
                      data-store-name-label
                      style={{
                        display: 'inline-block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                        lineHeight: 1,
                      }}
                    >
                      {shortenStoreName(storeName!)}
                    </span>
                  </a>
                )}
                {headerBadge}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Nav sections ── */}
      <div
        className="customized-scrollbar"
        style={{ flex: 1, overflowY: 'auto', paddingBottom: 8 }}
      >
        {sections.map((section, sIdx) => (
          <div key={sIdx}>
            {section.label && (
              <div
                style={{
                  padding: sIdx === 0 ? '12px 12px 4px' : '16px 12px 4px',
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: pantryColors.gray[400],
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                {section.label}
              </div>
            )}
            {section.items.map((item) => {
              const hasSubItems = (item.subItems?.length ?? 0) > 0
              const isParentOnly = hasSubItems && !item.onClick

              return (
                <div key={item.id}>
                  {/* Main item */}
                  <button
                    type="button"
                    onClick={() =>
                      isParentOnly ? undefined : item.onClick?.()
                    }
                    style={{
                      width: 'calc(100% - 16px)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: isParentOnly ? '8px 16px 4px' : '9px 16px',
                      borderRadius: pantryRadii.sm,
                      margin: '1px 8px',
                      border: 'none',
                      cursor: isParentOnly ? 'default' : 'pointer',
                      textAlign: 'left',
                      fontSize: isParentOnly ? 12 : 13.5,
                      fontWeight: isParentOnly ? 600 : item.active ? 600 : 400,
                      letterSpacing: isParentOnly ? '0.04em' : undefined,
                      textTransform: isParentOnly
                        ? ('uppercase' as const)
                        : undefined,
                      background:
                        isParentOnly || !item.active
                          ? 'transparent'
                          : pantryColors.brand.light,
                      color: isParentOnly
                        ? pantryColors.gray[500]
                        : item.active
                          ? pantryColors.brand.primary
                          : pantryColors.gray[600],
                      transition: 'background 0.15s, color 0.15s',
                      fontFamily: 'inherit',
                    }}
                    onMouseEnter={(e) => {
                      if (isParentOnly) return
                      if (!item.active) {
                        e.currentTarget.style.background =
                          pantryColors.gray[100]
                        e.currentTarget.style.color = pantryColors.gray[800]
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (isParentOnly) return
                      if (!item.active) {
                        e.currentTarget.style.background = 'transparent'
                        e.currentTarget.style.color = pantryColors.gray[600]
                      }
                    }}
                  >
                    {item.icon && (
                      <span
                        style={{
                          width: isParentOnly ? 15 : 16,
                          height: isParentOnly ? 15 : 16,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          opacity: isParentOnly ? 0.6 : item.active ? 1 : 0.75,
                        }}
                      >
                        <>{item.icon}</>
                      </span>
                    )}
                    <span
                      style={{
                        flex: '0 1 auto',
                        minWidth: 0,
                        display: 'inline-flex',
                        alignItems: 'flex-start',
                        gap: 3,
                      }}
                    >
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.label}
                      </span>
                      {item.badge != null && !hasSubItems && (
                        <span
                          style={{
                            flexShrink: 0,
                            alignSelf: 'flex-start',
                            minWidth: 15,
                            marginTop: -2,
                            height: 14,
                            padding: '0 4px',
                            borderRadius: 5,
                            background: pantryColors.brand.light,
                            color: pantryColors.brand.primary,
                            fontSize: 9,
                            fontWeight: 500,
                            lineHeight: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {item.badge}
                        </span>
                      )}
                    </span>
                  </button>

                  {/* Sub-items */}
                  {hasSubItems && (
                    <div style={{ marginBottom: 2 }}>
                      {item.subItems!.map((sub) => (
                        <button
                          key={sub.id}
                          type="button"
                          onClick={sub.onClick}
                          style={{
                            width: 'calc(100% - 16px)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 9,
                            padding: '8px 16px 8px 36px',
                            borderRadius: pantryRadii.sm,
                            margin: '1px 8px',
                            border: 'none',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontSize: 13,
                            fontWeight: sub.active ? 600 : 400,
                            background: sub.active
                              ? pantryColors.brand.light
                              : 'transparent',
                            color: sub.active
                              ? pantryColors.brand.primary
                              : pantryColors.gray[600],
                            transition: 'background 0.15s, color 0.15s',
                            fontFamily: 'inherit',
                          }}
                          onMouseEnter={(e) => {
                            if (!sub.active) {
                              e.currentTarget.style.background =
                                pantryColors.gray[100]
                              e.currentTarget.style.color =
                                pantryColors.gray[800]
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!sub.active) {
                              e.currentTarget.style.background = 'transparent'
                              e.currentTarget.style.color =
                                pantryColors.gray[600]
                            }
                          }}
                        >
                          {sub.icon && (
                            <span
                              style={{
                                width: 14,
                                height: 14,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                opacity: sub.active ? 1 : 0.7,
                              }}
                            >
                              {sub.icon}
                            </span>
                          )}
                          <span
                            style={{
                              flex: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {sub.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        ))}
      </div>

      {/* ── Version row (above user footer) ── */}
      {version && (
        <div
          style={{
            padding: '6px 12px 4px',
            flexShrink: 0,
            fontSize: 11,
            fontWeight: 400,
            letterSpacing: '0.02em',
            color: pantryColors.gray[400],
          }}
        >
          Current version: {version}
        </div>
      )}

      {/* ── User footer ── */}
      {user && (
        <div
          ref={userMenuRef}
          style={{
            padding: 12,
            borderTop: `1px solid ${pantryColors.gray[200]}`,
            flexShrink: 0,
            position: 'relative',
          }}
        >
          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={isUserMenuOpen}
            onClick={() => setIsUserMenuOpen((open) => !open)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: 8,
              borderRadius: pantryRadii.sm,
              border: 'none',
              background: isUserMenuOpen
                ? pantryColors.gray[100]
                : 'transparent',
              cursor: 'pointer',
              width: '100%',
              fontFamily: 'inherit',
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => {
              if (!isUserMenuOpen)
                e.currentTarget.style.background = pantryColors.gray[100]
            }}
            onMouseLeave={(e) => {
              if (!isUserMenuOpen)
                e.currentTarget.style.background = 'transparent'
            }}
          >
            <InitialsAvatar
              initials={user.initials}
              color={user.color}
              size="sm"
            />
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                textAlign: 'left',
                lineHeight: 1.3,
                overflow: 'hidden',
                flex: 1,
              }}
            >
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: pantryColors.gray[800],
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                }}
              >
                {user.name}
              </span>
              {user.role && (
                <span style={{ fontSize: 11, color: pantryColors.gray[400] }}>
                  {user.role}
                </span>
              )}
            </div>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              aria-hidden
              style={{
                marginLeft: 'auto',
                flexShrink: 0,
                color: pantryColors.gray[400],
                transform: isUserMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.15s',
              }}
            >
              <path
                d="M6 15l6-6 6 6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {isUserMenuOpen && (
            <div
              role="menu"
              style={{
                position: 'absolute',
                bottom: 'calc(100% - 4px)',
                left: 12,
                right: 12,
                background: pantryColors.white,
                border: `1px solid ${pantryColors.gray[200]}`,
                borderRadius: pantryRadii.md,
                boxShadow:
                  '0 12px 24px rgba(15, 23, 42, 0.12), 0 2px 4px rgba(15, 23, 42, 0.06)',
                padding: 6,
                zIndex: 50,
              }}
            >
              <UserMenuItem
                icon={
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    aria-hidden
                  >
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 21c0-4.418 3.582-8 8-8s8 3.582 8 8" />
                  </svg>
                }
                label="My account"
                onClick={handleMenuAction(user.onAccountClick)}
              />
              {user.onSwitchWorkspaceClick && (
                <UserMenuItem
                  icon={
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      aria-hidden
                    >
                      <path
                        d="M17 1l4 4-4 4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M3 11V9a4 4 0 014-4h14"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M7 23l-4-4 4-4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M21 13v2a4 4 0 01-4 4H3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  }
                  label="Switch workspace"
                  onClick={handleMenuAction(user.onSwitchWorkspaceClick)}
                />
              )}
              <div
                style={{
                  height: 1,
                  background: pantryColors.gray[200],
                  margin: '4px 0',
                }}
              />
              <UserMenuItem
                icon={
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    aria-hidden
                  >
                    <path
                      d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M16 17l5-5-5-5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M21 12H9"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                }
                label="Log out"
                tone="danger"
                onClick={handleMenuAction(user.onSignOutClick)}
              />
            </div>
          )}
        </div>
      )}
    </nav>
  )
}

interface UserMenuItemProps {
  icon: React.ReactNode
  label: string
  onClick?: () => void
  tone?: 'default' | 'danger'
}

const UserMenuItem: React.FC<UserMenuItemProps> = ({
  icon,
  label,
  onClick,
  tone = 'default',
}) => {
  const baseColor =
    tone === 'danger' ? pantryColors.red[700] : pantryColors.gray[700]
  const hoverColor =
    tone === 'danger' ? pantryColors.red[800] : pantryColors.gray[800]
  const hoverBg =
    tone === 'danger' ? pantryColors.red[50] : pantryColors.gray[100]
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        padding: '8px 10px',
        borderRadius: pantryRadii.sm,
        border: 'none',
        background: 'transparent',
        color: baseColor,
        cursor: onClick ? 'pointer' : 'default',
        fontFamily: 'inherit',
        fontSize: 13,
        fontWeight: 500,
        textAlign: 'left',
        transition: 'background 0.12s, color 0.12s',
      }}
      onMouseEnter={(e) => {
        if (!onClick) return
        e.currentTarget.style.background = hoverBg
        e.currentTarget.style.color = hoverColor
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent'
        e.currentTarget.style.color = baseColor
      }}
    >
      <span
        style={{
          width: 16,
          height: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </span>
      <span style={{ flex: 1 }}>{label}</span>
    </button>
  )
}
